import React, { useEffect, useState } from 'react';
import { Loading } from '@app/components/Loading';
import { INDEXDB_COMMON_KEYS } from '@app/consts/common_consts';
import { HttpStatusCode } from '@app/modules/error-handler/ErrorHandleModel';
import { Cache } from '@app/modules/cache';
import { UserUtils } from '@app/modules/userinfo';
import { authApiService } from '@app/services/authAps';
import { saveCurrentUrl } from '@app/modules/interceptors/NavigationInterceptor';
import { Endpoints } from '@settings/endpoints-setting';
import { Navigate } from 'react-router-dom';
import { NAVIGATE_ROUTE } from '../common-route';

export interface AuthGuardProps {
  children: React.ReactNode;
  /**
   * If true, user must be logged in to access
   * If false, user must NOT be logged in to access (e.g., login page)
   * If undefined, no auth check is performed
   */
  requireAuth?: boolean;
  /**
   * Custom redirect path when auth check fails
   */
  redirectTo?: string;
  /**
   * HTTP status code to pass when redirecting to error page
   */
  statusCode?: number;
}

/**
 * Authentication result with cache status
 */
export interface AuthCheckResult {
  isAuthenticated: boolean;
  hadCache: boolean; // true if user info existed in cache before check
}

/**
 * Check if user is authenticated by verifying user info
 * Returns both authentication status and whether cache existed
 */
export const isAuthenticated = async (): Promise<AuthCheckResult> => {
  try {
    // First check if we have user info in memory/cache
    const userInfo = UserUtils.get();
    const hasUserInfo = !!userInfo?.user?.userId;

    // Verify session with server
    const response = await authApiService.checkAuthentication();
    
    // If server says session is invalid, clear local user info and cache
    if (response?.success === false) {
      UserUtils.set(null);
      // Clear user info from cache
      await Cache.delete(INDEXDB_COMMON_KEYS.USER_INFO, 'indexdb');
      // Had cache but session expired
      return { isAuthenticated: false, hadCache: hasUserInfo };
    }

    // Both local and server checks passed
    return { isAuthenticated: true, hadCache: hasUserInfo };
  } catch (error) {
    // Check if we had cache before error
    const hadCache = !!UserUtils.get()?.user?.userId;
    // On error, clear user info and cache to be safe
    UserUtils.set(null);
    await Cache.delete(INDEXDB_COMMON_KEYS.USER_INFO, 'indexdb').catch(() => {
      // Ignore cache delete errors
    });
    return { isAuthenticated: false, hadCache };
  }
};

/**
 * AuthGuard component for protecting routes based on authentication status.
 *
 * Usage examples:
 * - <AuthGuard requireAuth={true}>...</AuthGuard> - Only authenticated users can access
 * - <AuthGuard requireAuth={false}>...</AuthGuard> - Only non-authenticated users can access
 * - <AuthGuard>...</AuthGuard> - No auth check, allows everyone
 */
const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth,
  redirectTo = NAVIGATE_ROUTE.SC014,
  statusCode = HttpStatusCode.Unauthorized,
}) => {
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string>(redirectTo);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      // If no auth requirement, skip check
      if (requireAuth === undefined) {
        if (isMounted) {
          setIsChecking(false);
        }
        return;
      }

      try {
        const authResult = await isAuthenticated();

        if (!isMounted) return;

        // If requireAuth is true and user is NOT authenticated, redirect
        if (requireAuth === true && authResult.isAuthenticated === false) {
          // Determine redirect based on cache status
          if (authResult.hadCache) {
            // Had cache but session expired → redirect to 401 error page
            setShouldRedirect(true);
            setRedirectPath(redirectTo); // Error page with 401 status
            setIsChecking(false);
          } else {
            // No cache (never logged in) → redirect to SSO
            saveCurrentUrl();
            window.location.href = `${Endpoints.COMMON.API_ENDPOINT}${NAVIGATE_ROUTE.SSO_AUTH}`;
            // Keep showing loading while redirect happens
            return;
          }
        } else {
          // User is authenticated or requireAuth is false
          setIsChecking(false);
        }
      } catch (error) {
        if (!isMounted) return;
        
        // On error, treat as unauthenticated if auth is required
        if (requireAuth === true) {
          // Redirect to SSO on error
          saveCurrentUrl();
          window.location.href = `${Endpoints.COMMON.API_ENDPOINT}${NAVIGATE_ROUTE.SSO_AUTH}`;
          return;
        }
        setIsChecking(false);
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [requireAuth, redirectTo, statusCode]);

  // Show loading while checking authentication
  if (isChecking) {
    return <Loading fullScreen blocked />;
  }

  // Redirect if authentication check failed
  if (shouldRedirect) {
    return <Navigate to={redirectPath} state={{ statusCode }} replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
